import {
  PrimaryButton,
  pxTovW,
  pxToRem,
  IconWrapper,
  IStyles,
  ImageWrapper,
  theme,
  useCommonServiceClientContext,
  getLocalStorage,
  getDifficultyLevelString,
  cloneObject,
  Loader,
  InputField,
  deserify,
} from '@geneo2-web/shared-ui';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from 'react-beautiful-dnd';
import { PaperDetailsCard } from '../components/PaperDetailsCard';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { getSubjectNameFromEnum } from '../../../Home/home.utils';
import {
  buildTaskInfoModels,
  getSubjectColorBySubjectId,
  getSubjectImageBySubjectId,
  getTotalMarksOfSection,
} from '../../utils/QPGv2.utils';
import {
  setQPGContent,
  setQPGFormatDetails,
  setQPGGeneratedTemplates,
  setQPGSelectedChapterData,
  setQPGSelectedSectionIndex,
  setQPGSelectedSectionNumber,
  setQPGSettingsConfig,
  setQPGSubsectionData,
} from '../../reducer/assessmentFlow.slice';
import { QPGSelectMultiSubjectsPopup } from '../../../ManageHomework/components/QPGSelectMultiSubjectsPopup';
import {
  ASSESMENT_LIBRARY,
  HOMEWORK_MANAGE,
  QPG_ASSESSMENT_CREATION,
  QPG_ASSESSMENT_PUBLISHED,
  QPG_ASSESSMENT_REVIEW_PAPER,
  QPG_PREVIEW,
} from '../../../../routeHandling/RoutesNomenclature';
import { setToastInfoV2 } from '../../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import {
  AssessmentContent,
  AssessmentTask,
} from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  SubSectionQuestionType,
  SubSectionTypeV2,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { InstructionPopup } from './components/InstructionPopup';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(20), md: pxTovW(50), lg: pxTovW(240) },
    paddingTop: { xs: pxToRem(20), md: pxTovW(50) },
    paddingBottom: { xs: pxToRem(70), md: pxTovW(50) },
    backgroundColor: '#f8f7fc',
    minHeight: '100vh',
    position: { xs: 'relative', md: 'unset' },
  },
  mainHeadingBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: { xs: pxToRem(20), md: pxTovW(30) },
    gap: { xs: pxToRem(10), md: pxTovW(50) },
  },
  assessNameBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(22) },
    boxSizing: 'border-box',
  },
  ellipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    paddingBottom: { xs: pxToRem(5), md: pxTovW(5) },
  },
  sectionTitleInput: {
    maxWidth: '100%',
    boxSizing: 'border-box',
    '& input': {
      maxWidth: '100%',
      width: '100%',
      height: { xs: pxToRem(25), md: pxTovW(54) },
      fontFamily: 'Poppins',
      fontSize: { xs: pxToRem(18), md: pxTovW(40) },
    },
    '& .MuiOutlinedInput-root': {
      padding: '0',
      borderBottom: `2px solid #007CDC`,
      backgroundColor: 'transparent',
      paddingX: { xs: pxToRem(10), md: pxTovW(10) },
      borderRadius: '0',
    },
  },
  previewAndPublishBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
  },
  previewBox: {
    backgroundColor: '#FFF6D4',
    border: '2px solid #F8C807',
    borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
    cursor: 'pointer',
    boxSizing: 'border-box',
    width: { xs: pxToRem(28), md: pxTovW(60), lg: pxTovW(47) },
    height: { xs: pxToRem(28), md: pxTovW(60), lg: pxTovW(47) },
    display: { xs: 'none', md: 'flex' },
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
      backgroundColor: '#FFF6D4',
    },
  },
  editButton: {
    padding: '0',
    minWidth: 'unset',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
      backgroundColor: 'transparent',
    },
  },
  editIcon: {
    height: { xs: pxToRem(21), md: pxTovW(30) },
    width: { xs: pxToRem(21), md: pxTovW(30) },
    border: '1px solid #0AA34F',
    borderRadius: { xs: pxToRem(6), md: pxTovW(6) },
  },
  publishButton: {
    width: { xs: '90%', md: pxTovW(515) },
    height: { xs: pxToRem(55), md: pxTovW(77) },
    // fontFamily: 'poppins',
    // fontSize: { xs: pxToRem(21), md: pxTovW(21) },
    // fontWeight: 'bold',
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    position: { xs: 'fixed', md: 'unset' },
    bottom: { xs: pxToRem(5), md: 'unset' },
    left: { xs: '5%', md: 'unset' },
    zIndex: { xs: 10, md: 0 },
  },
  classNameAddMoreBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', md: 'center' },
    marginBottom: { xs: pxToRem(20), md: pxTovW(30) },
    gap: { xs: pxToRem(25), md: pxTovW(0) },
  },
  secondaryHeadingBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(25) },
    alignItems: 'center',
  },
  classNamePill: {
    border: '1px solid #5DACF7',
    backgroundColor: '#DDFAEC',
    borderRadius: { xs: pxToRem(25), md: pxTovW(25) },
    paddingX: { xs: pxToRem(10), md: pxTovW(25) },
    paddingY: { xs: pxToRem(5), md: pxTovW(10) },
  },
  addInstructionsBox: {
    background: '#CFEAFF',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #007CDC',
    padding: {
      xs: pxToRem(5),
      md: `${pxTovW(16)} ${pxTovW(23)}`,
      lg: `${pxTovW(13)} ${pxTovW(25)}`,
    },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    height: { xs: pxToRem(28), md: pxTovW(57) },
  },
  addMoreBox: {
    background: '#C1F8DA',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #0AA34F',
    padding: {
      xs: pxToRem(5),
      md: `${pxTovW(16)} ${pxTovW(23)}`,
      lg: `${pxTovW(13)} ${pxTovW(25)}`,
    },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    height: { xs: pxToRem(28), md: pxTovW(57) },
  },
};

export const QPGPublish = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const teacherId = getLocalStorage('userId');
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessment_id');
  const { setSelectedFunction } = useGlobalContext();
  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const user_info = useDeserifiedSelector((state) => state.auth.user_info);
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { generated_qpg_templates } = useAppSelector(
    (state) => state.assessment
  );
  const qpg_format_details = useDeserifiedSelector(
    (state) => state.assessment.qpg_format_details
  );
  const qpg_content = useDeserifiedSelector(
    (state) => state.assessment.qpg_content
  );

  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const [qpgNameEdit, setQpgNameEdit] = useState<boolean>(false);
  const [multiSubjectsPopup, setMultiSubjectsPopup] = useState<boolean>(false);
  const [addInstructionsPopup, setAddInstructionsPopup] =
    useState<boolean>(false);
  const [instructions, setInstructions] = useState<string>('');

  // ----------------------------------------------------

  const fetchSectionTemplates = async () => {
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getQuestionPaperTemplate(
          {
            personId: BigInt(teacherId),
            taskType: TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM,
          }
        );
      if (response.questionPaperTemplates) {
        dispatch(setQPGGeneratedTemplates(response.questionPaperTemplates));
      }
    } catch (error) {
      setLoading('error');
      dispatch(
        setToastInfoV2({
          label: 'Error while fetching section templates',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ----------------------------------------------------

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || !qpg_content) return;
      const { source, destination } = result;
      if (source.index === destination.index) return;
      const sections = [...qpg_content.multiSubjectSections];
      const [removed] = sections.splice(source.index, 1);
      sections.splice(destination.index, 0, removed);
      // Update sectionNo to match new order (1-based)
      const sectionsWithUpdatedSectionNo = sections.map(
        (section: MultiSubjectSection, index: number) => {
          const cloned = cloneObject(section);
          cloned.sectionNo = index + 1;
          return cloned;
        }
      );
      const updatedContent = cloneObject(qpg_content);
      updatedContent.multiSubjectSections = sectionsWithUpdatedSectionNo;
      dispatch(setQPGContent(updatedContent));
      handlePublishClick({ updatedContent: updatedContent });
    },
    [dispatch, qpg_content]
  );

  // ----------------------------------------------------

  const handleDeleteCard = useCallback(
    (index: number) => {
      if (!qpg_content) return;
      if ((qpg_content.multiSubjectSections?.length ?? 0) <= 1) {
        dispatch(
          setToastInfoV2({
            label: 'At least one item should be in the Question paper.',
            variant: 'info',
            open: true,
          })
        );
        return;
      }
      const updatedContent = cloneObject(qpg_content);
      updatedContent.multiSubjectSections = qpg_content.multiSubjectSections
        .filter((_, i) => i !== index)
        .map(
          (item, idx) =>
            new MultiSubjectSection({ ...item, sectionNo: idx + 1 })
        );
      dispatch(setQPGContent(updatedContent));

      // dispatch(
      //   setQPGFormatDetails({
      //     ...(updatedContent?.assessment?.classId != null && {
      //       selectedClass: {
      //         name: `Class ${updatedContent?.assessment?.class}`,
      //         id: updatedContent.assessment.classId,
      //       },
      //     }),
      //     selectedSubjectsAndMarks:
      //       updatedContent?.multiSubjectSections?.map((item) => ({
      //         subject:
      //           item.subjectId != null
      //             ? {
      //                 name: getSubjectNameFromEnum(item.subject),
      //                 id: item.subjectId,
      //               }
      //             : null,
      //         marks: item.marks,
      //       })) ?? [],
      //     assessmentName: updatedContent?.assessment?.assessmentTitle ?? '',
      //   })
      // );
      handlePublishClick({ updatedContent: updatedContent });
    },
    [dispatch, qpg_content]
  );

  // ----------------------------------------------------

  const changeSectionTitle = (sectionIndex: number, title: string) => {
    if (!qpg_content?.multiSubjectSections?.length) return;
    if (
      sectionIndex < 0 ||
      sectionIndex >= qpg_content.multiSubjectSections.length
    )
      return;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections = qpg_content.multiSubjectSections.map(
      (section, i) =>
        i === sectionIndex
          ? new MultiSubjectSection({ ...section, sectionTitle: title })
          : section
    );
    dispatch(setQPGContent(updatedContent));
  };

  // ----------------------------------------------------

  const previewAssessment = () => {
    navigate(QPG_PREVIEW);
  };

  // ----------------------------------------------------

  const handlePublishClick = async (props: {
    publishClick?: boolean;
    updatedContent?: AssessmentContent;
  }) => {
    const { publishClick, updatedContent } = props;

    const selectedSubjectsAndMarks = updatedContent?.multiSubjectSections?.map(
      (item) => ({
        subject:
          item.subjectId != null
            ? {
                name: getSubjectNameFromEnum(item.subject),
                id: item.subjectId,
              }
            : null,
        marks: item.totalMarks,
      })
    );

    let metaInfo: MultiSubjectSection[] = [];
    if (updatedContent) {
      metaInfo = updatedContent?.multiSubjectSections?.map(
        (section: MultiSubjectSection) =>
          new MultiSubjectSection({
            ...section,
            // if no status is then: INITIALIZED
            multiSubjectSectionStatus: section.multiSubjectSectionStatus
              ? section.multiSubjectSectionStatus
              : MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED,
          })
      );
    } else {
      metaInfo = qpg_content?.multiSubjectSections ?? [];
    }

    const selectedTasksInfo = buildTaskInfoModels(metaInfo);
    const selectedQuestionIds = new Set(
      selectedTasksInfo.map((t) => t.questionId).filter(Boolean)
    );
    const questionModuleMap = (
      qpg_content?.questionsSequenceInfo?.questionModuleMap ?? []
    ).filter(
      (m) => m.questionId != null && selectedQuestionIds.has(m.questionId)
    );

    try {
      setLoading('loading');
      const assessmentCreationSubmitResponse =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
          {
            teacherId: BigInt(teacherId),
            assessmentId: assessmentId ? BigInt(assessmentId) : undefined,
            classId: qpg_format_details?.selectedClass?.id,
            creationStatus: publishClick
              ? TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
              : TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
            multiSubjectSectionMetaInfo: metaInfo,
            sectionId: class_subject_info?.sectionId,
            subjectIds: metaInfo?.map((section) => section.subjectId) ?? [],
            taskName: qpg_content?.assessment?.assessmentTitle ?? '',
            taskType: TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM,
            instructions: [instructions],
            questionModuleMap: questionModuleMap,
            selectedTasksInfo: selectedTasksInfo,
            chapterIdsInfo:
              qpg_content?.assessment?.taskMetaInfo?.chapterInfo
                ?.chapterTopicsInfo,
          }
        );

      if (assessmentCreationSubmitResponse.status === 200) {
        if (publishClick) {
          navigate(
            `${QPG_ASSESSMENT_PUBLISHED}?assessment_id=${assessmentCreationSubmitResponse.data?.assessment?.assessmentId}`
          );
        } else {
          await fetchAssessmentContent();
        }
      } else {
        dispatch(
          setToastInfoV2({
            label: 'Error while creating assessment',
            variant: 'error',
            open: true,
          })
        );
      }
      setLoading('success');
    } catch (error) {
      setLoading('success');
      console.log('error:', error);
      let message = 'Error while creating assessment';
      if (error instanceof ConnectError) {
        message = error.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      dispatch(
        setToastInfoV2({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ----------------------------------------------------

  const fetchAssessmentContent = async () => {
    try {
      setLoading('loading');
      const fetchAssessmentContentResponse =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacherId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: assessmentId ? BigInt(assessmentId) : undefined,
          }
        );

      if (fetchAssessmentContentResponse.data) {
        setLoading('success');

        dispatch(setQPGContent(fetchAssessmentContentResponse.data));
        setInstructions(
          fetchAssessmentContentResponse.data?.assessment
            ?.assessmentInstructions[0] || ''
        );

        const selectedSubjectsAndMarks =
          fetchAssessmentContentResponse.data?.multiSubjectSections?.map(
            (item) => ({
              subject:
                item.subjectId != null
                  ? {
                      name: getSubjectNameFromEnum(item.subject),
                      id: item.subjectId,
                    }
                  : null,
              marks: item.preferredMarks,
            })
          ) ?? [];

        dispatch(
          setQPGFormatDetails({
            ...qpg_format_details,
            ...(fetchAssessmentContentResponse.data?.assessment?.classId !=
              null && {
              selectedClass: {
                name: `Class ${fetchAssessmentContentResponse.data?.assessment?.class}`,
                id: fetchAssessmentContentResponse.data?.assessment.classId,
              },
            }),
            selectedSubjectsAndMarks: selectedSubjectsAndMarks,
            assessmentName:
              fetchAssessmentContentResponse.data?.assessment
                ?.assessmentTitle ?? '',
          })
        );
      } else {
        setLoading('error');
      }
    } catch (error) {
      console.log(error);
      setLoading('error');
    }
  };

  // ----------------------------------------------------

  const openMultiSubjectsPopup = () => {
    setMultiSubjectsPopup(true);
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        ...(qpg_content?.assessment?.classId != null && {
          selectedClass: {
            name: `Class ${qpg_content?.assessment?.class}`,
            id: qpg_content.assessment.classId,
          },
        }),
        selectedSubjectsAndMarks:
          qpg_content?.multiSubjectSections?.map((item) => ({
            subject:
              item.subjectId != null
                ? {
                    name: getSubjectNameFromEnum(item.subject),
                    id: item.subjectId,
                  }
                : null,
            marks: item.preferredMarks,
          })) ?? [],
        assessmentName: qpg_content?.assessment?.assessmentTitle ?? '',
      })
    );
  };

  // ----------------------------------------------------

  const getTotalQuestionNumber = (sectionIndex: number) => {
    const indexData = qpg_content?.multiSubjectSections[sectionIndex];
    if (
      indexData?.templateId &&
      (indexData?.multiSubjectSectionStatus ===
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_UNDEFINED ||
        indexData?.multiSubjectSectionStatus ===
          MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED)
    ) {
      return generated_qpg_templates?.find(
        (template) =>
          Number(template.questionPaperTemplateId) === indexData?.templateId
      )?.multiSubjectSectionTemplate?.noOfQuestions;
    }

    return indexData?.multiSubjectSubSections.reduce(
      (acc, curr) => acc + curr.subSectionMetaData.length,
      0
    );
  };

  // ----------------------------------------------------

  const publishButtonDisabled = useMemo(() => {
    return qpg_content?.multiSubjectSections.some(
      (section) =>
        section.multiSubjectSectionStatus !==
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_APPROVED
    );
  }, [qpg_content]);

  // ----------------------------------------------------

  const backClickHandler = () => {
    if (location.state?.fromAssessmentLibrary) {
      navigate(ASSESMENT_LIBRARY, { state: { fromQPGPublish: true } });
    } else {
      navigate(HOMEWORK_MANAGE);
    }
  };

  useEffect(() => {
    if (assessmentId) {
      fetchAssessmentContent();
    }

    fetchSectionTemplates();
  }, [assessmentId]);

  useEffect(() => {
    setSelectedFunction(() => backClickHandler);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // ----------------------------------------------------

  if (loading === 'loading') return <Loader />;

  // if (loading === 'error') return <NoContentCard variant="error" icon="error" text="Error Occurred" />;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.mainHeadingBox}>
        <Box sx={styles.assessNameBox}>
          {qpgNameEdit ? (
            <InputField
              variant="outlined"
              fullWidth
              placeholder="Add Section Title"
              type="text"
              value={qpg_content?.assessment?.assessmentTitle ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                // setEditingAssessmentTitle(e.target.value);
                if (!qpg_content) return;
                const updatedContent = cloneObject(qpg_content);
                if (updatedContent.assessment) {
                  updatedContent.assessment = new AssessmentTask({
                    ...updatedContent.assessment,
                    assessmentTitle: e.target.value,
                  });
                }
                dispatch(setQPGContent(updatedContent));
              }}
              onBlur={() => {
                handlePublishClick({});
                setQpgNameEdit(false);
              }}
              sx={styles.sectionTitleInput}
            />
          ) : (
            <Typography variant="h1" sx={styles.ellipsisText}>
              {qpg_content?.assessment?.assessmentTitle}
            </Typography>
          )}

          <Button
            onClick={() => {
              if (qpgNameEdit) {
                handlePublishClick({});
              }
              setQpgNameEdit((prev) => !prev);
            }}
            sx={styles.editButton}
          >
            <IconWrapper
              name={qpgNameEdit ? 'correct-green-square' : 'edit'}
              parentFolder="icons"
              type="png"
              size="md"
              customSx={styles.editIcon}
            />
          </Button>
        </Box>

        <Box sx={styles.previewAndPublishBox}>
          <Button onClick={previewAssessment} sx={styles.previewBox}>
            <ImageWrapper
              name="eye-yellow"
              parentFolder="icons"
              type="png"
              styles={{
                width: { xs: pxToRem(15), md: pxTovW(24) },
                height: { xs: pxToRem(10), md: pxTovW(18) },
              }}
            />
          </Button>
          <PrimaryButton
            variant="contained"
            disabled={publishButtonDisabled}
            onClick={() => handlePublishClick({ publishClick: true })}
            sx={styles.publishButton}
          >
            <Typography variant="h3" fontWeight="bold" color="common.white">
              PUBLISH
            </Typography>
          </PrimaryButton>
        </Box>
      </Box>

      <Box sx={styles.classNameAddMoreBox}>
        <Box sx={styles.secondaryHeadingBox}>
          <Typography variant="h2">Your Added Subjects</Typography>

          <Box sx={styles.classNamePill}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              Class {qpg_content?.assessment?.class}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: pxToRem(10), md: pxTovW(20) } }}>
          <Button
            onClick={previewAssessment}
            sx={{ ...styles.previewBox, display: { xs: 'flex', md: 'none' } }}
          >
            <ImageWrapper
              name="eye-yellow"
              parentFolder="icons"
              type="png"
              styles={{
                width: { xs: pxToRem(15), md: pxTovW(24) },
                height: { xs: pxToRem(10), md: pxTovW(18) },
              }}
            />
          </Button>

          <Button
            sx={styles.addInstructionsBox}
            onClick={() => {
              setAddInstructionsPopup(true);
            }}
          >
            <AddCircleIcon
              sx={{
                color: 'primary.main',
                fontSize: { xs: pxToRem(18), md: pxTovW(30) },
              }}
            />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              fontWeight="bold"
              color="primary.main"
            >
              {(qpg_content?.assessment?.assessmentInstructions?.length ?? 0) >
                0 && qpg_content?.assessment?.assessmentInstructions?.[0] !== ''
                ? 'EDIT INSTRUCTIONS'
                : 'ADD INSTRUCTIONS'}
            </Typography>
          </Button>

          <Button
            sx={styles.addMoreBox}
            onClick={() => {
              openMultiSubjectsPopup();
            }}
          >
            <AddCircleIcon
              sx={{
                color: '#03612D',
                fontSize: { xs: pxToRem(18), md: pxTovW(30) },
              }}
            />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              fontWeight="bold"
              color="secondary.main"
            >
              ADD MORE
            </Typography>
          </Button>
        </Box>
      </Box>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="paper-details-cards">
          {(droppableProvided) => (
            <Box
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: pxToRem(10), md: pxTovW(30) },
              }}
            >
              {qpg_content?.multiSubjectSections.map((item, index) => (
                <Draggable
                  key={index}
                  draggableId={index.toString()}
                  index={index}
                >
                  {(draggableProvided) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <PaperDetailsCard
                        totalSections={qpg_content?.multiSubjectSections.length}
                        subjectIcon={getSubjectImageBySubjectId(
                          item.subjectId,
                          user_info?.teachClassSubjects
                        )}
                        subjectColor={getSubjectColorBySubjectId(
                          item.subjectId,
                          user_info?.teachClassSubjects
                        )}
                        subjectName={getSubjectNameFromEnum(item.subject)}
                        marks={
                          item.multiSubjectSectionStatus ===
                            MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_UNDEFINED ||
                          item.multiSubjectSectionStatus ===
                            MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED
                            ? item.preferredMarks
                            : getTotalMarksOfSection(item)
                        }
                        reorderCardHandler={() => {
                          // console.log('reorder card');
                        }}
                        deleteCardHandler={() => handleDeleteCard(index)}
                        sectionTitle={item.sectionTitle}
                        onChangeSectionTitle={(value: string) =>
                          changeSectionTitle(index, value)
                        }
                        onSectionTitleEditDone={async () => {
                          await handlePublishClick({});
                        }}
                        chapterList={item.chapterInfo?.chapterTopicsInfo}
                        noOfQuestions={getTotalQuestionNumber(index)}
                        difficultyLevel={
                          item.avgDifficultyLevel ===
                          DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED
                            ? '-'
                            : getDifficultyLevelString(item.avgDifficultyLevel)
                        }
                        createAssessmentHandler={() => {
                          dispatch(setQPGSelectedSectionIndex(index));
                          dispatch(setQPGSelectedSectionNumber(item.sectionNo));
                          dispatch(setQPGSelectedChapterData(undefined));
                          dispatch(setQPGSettingsConfig(undefined));
                          dispatch(
                            setQPGSubsectionData(
                              (item.multiSubjectSubSections ?? []).map(
                                (sub, subIdx) =>
                                  new SubSectionTypeV2({
                                    subSectionName: sub.subSectionName,
                                    sectionNumber:
                                      sub.subSectionNo ?? subIdx + 1,
                                    passageText: sub.passageText ?? [],
                                    passageImagesUpload:
                                      sub.passageImagesUpload ?? [],
                                    ...(sub.isAiGeneratedPassage !== undefined
                                      ? {
                                          isAiGeneratedPassage:
                                            sub.isAiGeneratedPassage,
                                        }
                                      : {}),
                                    ...(sub.attemptAnyCount !== undefined
                                      ? { attemptAnyCount: sub.attemptAnyCount }
                                      : {}),
                                    ...(sub.subSectionType !== undefined
                                      ? { subSectionType: sub.subSectionType }
                                      : {}),
                                    subSectionQuestions: (
                                      sub.questionTypeAndMarks ?? []
                                    ).map(
                                      (qtm) =>
                                        new SubSectionQuestionType({
                                          questionType: qtm.questionType,
                                          marks: qtm.marks,
                                          noOfQuestion: 0,
                                          isAlternateEnable: false,
                                        })
                                    ),
                                  })
                              )
                            )
                          );
                          if (
                            item.multiSubjectSectionStatus ===
                              MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_UNDEFINED ||
                            item.multiSubjectSectionStatus ===
                              MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED
                          ) {
                            navigate(QPG_ASSESSMENT_CREATION);
                          } else {
                            navigate(QPG_ASSESSMENT_REVIEW_PAPER);
                          }
                        }}
                        assessmentStatus={item.multiSubjectSectionStatus}
                        dragHandleProps={draggableProvided.dragHandleProps}
                        published={false}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {multiSubjectsPopup ? (
        <QPGSelectMultiSubjectsPopup
          open={multiSubjectsPopup}
          handleClose={() => {
            setMultiSubjectsPopup(false);
          }}
          onProceedClick={() => {
            setMultiSubjectsPopup(false);
            if (assessmentId) {
              fetchAssessmentContent();
            }
          }}
          sectionMetaInfo={qpg_content?.multiSubjectSections}
        />
      ) : null}

      <InstructionPopup
        open={addInstructionsPopup}
        handleClose={() => {
          setAddInstructionsPopup(false);
          setInstructions(
            qpg_content?.assessment?.assessmentInstructions[0] ?? ''
          );
        }}
        instructions={instructions}
        onChange={(e: string | undefined) => {
          setInstructions(e || '');
        }}
        addInstructionHandler={async () => {
          await handlePublishClick({});
          setAddInstructionsPopup(false);
        }}
      />
    </Box>
  );
};

// =====================================================
